(() => {
  if (customElements.get('zk-collection-videos')) return;
  const parseTime = value => {
    if (!value) return 0;
    if (/^\d+s?$/.test(value)) return Number.parseInt(value, 10);
    const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(value);
    return match ? Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0) : 0;
  };
  const parseVideo = raw => {
    try {
      const url = new URL(raw);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
      let id;
      if (host === 'youtu.be') id = url.pathname.split('/')[1];
      else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        if (url.pathname === '/watch') id = url.searchParams.get('v');
        else if (/^\/(shorts|embed|live)\//.test(url.pathname)) id = url.pathname.split('/')[2];
      } else if (host === 'tiktok.com') {
        const match = /^\/@[^/]+\/video\/(\d+)\/?$/.exec(url.pathname);
        return match ? { platform: 'tiktok', id: match[1], embed: 'https://www.tiktok.com/player/v1/' + match[1] + '?autoplay=1' } : null;
      }
      if (!/^[A-Za-z0-9_-]{11}$/.test(id || '')) return null;
      const start = parseTime(url.searchParams.get('start') || url.searchParams.get('t') || new URLSearchParams(url.hash.slice(1)).get('t'));
      return { platform: 'youtube', id, start, embed: 'https://www.youtube.com/embed/' + id + '?autoplay=1&playsinline=1&rel=0&start=' + start };
    } catch { return null; }
  };
  let lightboxLoading;
  const loadLightbox = src => {
    if (window.FsLightbox) return Promise.resolve();
    if (lightboxLoading) return lightboxLoading;
    lightboxLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => window.FsLightbox ? resolve() : reject(new Error('FsLightbox unavailable'));
      script.onerror = reject;
      document.head.append(script);
    }).catch(error => { lightboxLoading = null; throw error; });
    return lightboxLoading;
  };
  class CollectionVideos extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal });
      this.track = this.querySelector('.zk-cvr__track');
      this.cards = [...this.querySelectorAll('.zk-cvr__card')];
      this.progress = this.querySelector('.zk-cvr__progress');
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.status = this.querySelector('[role=status]');
      this.drag = null;
      on(this, 'click', event => {
        const filter = event.target.closest('[data-filter]');
        if (filter) this.filter(filter.dataset.filter);
        const play = event.target.closest('[data-video-url]');
        if (play && !this.suppressClick) this.openVideo(play);
        if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; }
      });
      on(this.previous, 'click', () => this.move(-1));
      on(this.next, 'click', () => this.move(1));
      on(this.track, 'scroll', () => this.updateProgress(), { passive: true });
      on(this.progress, 'input', () => {
        this.track.scrollLeft = Number(this.progress.value) / 1000 * (this.track.scrollWidth - this.track.clientWidth);
      });
      on(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.track.scrollTo({ left: 0, behavior: this.motion() });
        else if (event.key === 'End') this.track.scrollTo({ left: this.track.scrollWidth, behavior: this.motion() });
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(this.track, 'dragstart', event => event.preventDefault());
      on(this.track, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.suppressClick = false;
        this.drag = { x: event.clientX, left: this.track.scrollLeft, id: event.pointerId };
      });
      on(window, 'pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 6) {
          this.suppressClick = true;
          this.track.dataset.dragging = '';
          this.track.scrollLeft = this.drag.left - distance;
          event.preventDefault();
        }
      });
      const endDrag = () => { this.drag = null; delete this.track.dataset.dragging; };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(document, 'shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId);
        if (card) { this.filter('all'); this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: this.motion() }); }
      });
      this.observer = new ResizeObserver(() => this.updateProgress());
      this.observer.observe(this.track);
      this.cards.forEach(card => {
        const video = parseVideo(card.querySelector('[data-video-url]').dataset.videoUrl);
        const placeholder = card.querySelector('[data-cover-fallback]');
        if (placeholder && video?.platform === 'youtube') {
          const image = document.createElement('img');
          image.className = 'zk-cvr__cover';
          image.width = 640;
          image.height = 360;
          image.loading = 'lazy';
          image.alt = card.querySelector('h3').textContent;
          image.src = 'https://i.ytimg.com/vi/' + video.id + '/hqdefault.jpg';
          placeholder.replaceWith(image);
        }
        if (this.dataset.autoMetadata === 'true' && video?.platform === 'youtube') this.metadata(card, video, signal);
      });
      this.updateProgress();
    }
    disconnectedCallback() {
      this.closeVideo();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.drag = null;
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    filter(model) {
      this.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === model)));
      this.cards.forEach(card => { card.hidden = model !== 'all' && card.dataset.model !== model; });
      this.track.scrollLeft = 0;
      this.updateProgress();
    }
    move(direction) {
      const card = this.cards.find(item => !item.hidden);
      if (!card) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.motion() });
    }
    updateProgress() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.progress.value = max > 0 ? String(Math.round(this.track.scrollLeft / max * 1000)) : '0';
      this.progress.disabled = max < 2;
      this.previous.disabled = this.track.scrollLeft < 2;
      this.next.disabled = this.track.scrollLeft >= max - 2;
    }
    async metadata(card, video, signal) {
      const title = card.querySelector('[data-manual-title]');
      const author = card.querySelector('[data-author]');
      if (title.dataset.manualTitle && author.dataset.manualAuthor) return;
      try {
        const response = await fetch('https://www.youtube.com/oembed?format=json&url=' + encodeURIComponent('https://www.youtube.com/watch?v=' + video.id), { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) });
        if (!response.ok) return;
        const data = await response.json();
        if (signal.aborted || !this.isConnected) return;
        if (!title.dataset.manualTitle && data.title) title.textContent = data.title;
        if (!author.dataset.manualAuthor && data.author_name) author.textContent = data.author_name;
      } catch {}
    }
    async openVideo(trigger) {
      const video = parseVideo(trigger.dataset.videoUrl);
      if (!video) { this.status.textContent = this.querySelector('[data-error-label]').textContent; return; }
      if (this.opening) return;
      this.opening = true;
      const signal = this.controller.signal;
      trigger.setAttribute('aria-busy', 'true');
      try {
        await loadLightbox(this.dataset.fslightboxSrc);
        if (signal.aborted || !this.isConnected) return;
        this.closeVideo();
        this.trigger = trigger;
        this.frame = document.createElement('iframe');
        this.frame.className = 'zk-cvr-modal-frame' + (video.platform === 'tiktok' ? ' zk-cvr-modal-frame--tiktok' : '');
        this.frame.title = trigger.closest('article').querySelector('h3').textContent;
        this.frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        this.frame.allowFullscreen = true;
        this.frame.referrerPolicy = 'strict-origin-when-cross-origin';
        this.frame.src = video.embed;
        this.lightbox = new window.FsLightbox();
        this.lightbox.props.sources = [this.frame];
        this.lightbox.props.types = ['custom'];
        const instance = this.lightbox;
        this.lightbox.props.onOpen = () => this.prepareModal(instance);
        this.lightbox.props.onClose = () => { if (this.lightbox === instance) this.releaseModal(); };
        this.lightbox.open();
        this.status.textContent = '';
      } catch { this.status.textContent = this.querySelector('[data-error-label]').textContent; }
      finally { this.opening = false; trigger.removeAttribute('aria-busy'); }
    }
    prepareModal(instance) {
      const container = instance.elements.container;
      this.modal = container;
      container.classList.add('zk-cvr-modal');
      container.setAttribute('role', 'dialog');
      container.setAttribute('aria-modal', 'true');
      container.setAttribute('aria-label', this.frame.title);
      const toolbar = [...container.querySelectorAll('.fslightbox-toolbar-button')];
      toolbar.forEach(button => {
        button.setAttribute('role', 'button');
        button.tabIndex = 0;
        button.setAttribute('aria-label', button.title === 'Close' ? this.querySelector('[data-close-label]').textContent : button.title);
      });
      this.modalController = new AbortController();
      container.addEventListener('keydown', event => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.classList.contains('fslightbox-toolbar-button')) { event.preventDefault(); event.target.click(); }
        if (event.key === 'Escape') { event.preventDefault(); this.closeVideo(); }
        if (event.key === 'Tab') {
          const focusable = [...container.querySelectorAll('[tabindex="0"], iframe')];
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }, { signal: this.modalController.signal });
      document.addEventListener('focusin', event => {
        if (this.modal && !container.contains(event.target)) toolbar[toolbar.length - 1]?.focus();
      }, { signal: this.modalController.signal });
      toolbar[toolbar.length - 1]?.focus();
    }
    releaseModal() {
      this.modalController?.abort();
      if (this.frame) { this.frame.src = 'about:blank'; this.frame.remove(); this.frame = null; }
      this.modal = null;
      const trigger = this.trigger;
      this.trigger = null;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      this.lightbox = null;
    }
    closeVideo() {
      const instance = this.lightbox;
      if (instance) instance.close();
      this.releaseModal();
    }
  }
  customElements.define('zk-collection-videos', CollectionVideos);
})();
